import { FC, useCallback, useEffect, useState } from "react"
import { trackAssistantEvent } from "../../utils/assistant-analytics"
import { Check, Loader2, RefreshCw, ShieldAlert, X } from "lucide-react"
import {
  approvePendingAction,
  getPendingAction,
  PendingActionRequestError,
  rejectPendingAction
} from "../../../api/staff-agent"
import { StaffPendingAction } from "../../hooks/use-staff-chat-stream"
import { ChangesDiff, isPlainObject, PayloadValue } from "./payload-view"
import ActionOutcomeFeedback from "./action-outcome-feedback"

interface Props {
  action: StaffPendingAction
  onStatusChange: (actionId: string, status: string) => void
  conversationId?: string
}

/**
 * Inline approval card for a write action the agent proposed. The agent never
 * executes writes itself — execution only happens when a human clicks Approve
 * here (POST /api/actions/:id/approve on bb-admin-agent).
 */
const ApprovalCard: FC<Props> = ({
  action,
  onStatusChange,
  conversationId
}) => {
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    try {
      const refreshed = await getPendingAction(action.id)
      onStatusChange(action.id, refreshed.status)
      if (refreshed.status !== "execution_unknown") setErrorMsg(null)
    } catch (error: any) {
      setErrorMsg(error?.message || "Could not refresh action status")
    }
  }, [action.id, onStatusChange])

  useEffect(() => {
    if (action.status !== "executing") return
    const timer = window.setInterval(refreshStatus, 3000)
    return () => window.clearInterval(timer)
  }, [action.status, refreshStatus])

  const decide = async (decision: "approve" | "reject") => {
    setBusy(decision)
    setErrorMsg(null)
    trackAssistantEvent(
      decision === "approve"
        ? "action_approve_clicked"
        : "action_reject_clicked",
      { actionType: action.actionType ?? "" }
    )
    try {
      const result =
        decision === "approve"
          ? await approvePendingAction(action.id)
          : await rejectPendingAction(action.id)
      onStatusChange(action.id, result.status ?? decision)
    } catch (error: any) {
      setErrorMsg(error?.message || `Failed to ${decision} action`)
      if (decision === "approve") {
        const knownStatus =
          error instanceof PendingActionRequestError
            ? error.actionStatus
            : undefined
        // A dropped approval response may have happened after the upstream
        // mutation committed. Never invite a blind retry by calling it failed.
        onStatusChange(action.id, knownStatus || "execution_unknown")
      }
    } finally {
      setBusy(null)
    }
  }

  // Update-style payloads ({changes, previous}) render as a before → after
  // diff; changes/previous are then omitted from the generic key/value rows.
  const payload = action.payload ?? {}
  const changes = isPlainObject(payload.changes) ? payload.changes : null
  const previous = isPlainObject(payload.previous) ? payload.previous : null
  const payloadEntries = Object.entries(payload).filter(
    ([key, value]) =>
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !(changes && (key === "changes" || key === "previous"))
  )

  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-start gap-2.5">
        <ShieldAlert
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-800 leading-snug">
            Approval required
          </p>
          <p className="mt-0.5 text-sm text-neutral-700">{action.summary}</p>
          {payloadEntries.length > 0 && (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {payloadEntries.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-xs text-neutral-500 whitespace-nowrap">
                    {key}
                  </dt>
                  <dd className="text-xs text-neutral-800 break-all">
                    <PayloadValue value={value} />
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {changes && <ChangesDiff changes={changes} previous={previous} />}

          {action.status === "proposed" && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => decide("approve")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
              >
                {busy === "approve" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Approve
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => decide("reject")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
              >
                {busy === "reject" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <X size={14} />
                )}
                Reject
              </button>
            </div>
          )}

          {action.status === "executed" && (
            <>
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700">
                <Check size={14} /> Executed
              </p>
              <ActionOutcomeFeedback
                actionId={action.id}
                conversationId={conversationId}
                initial={action.feedback}
              />
            </>
          )}
          {action.status === "executing" && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-violet-700">
              <Loader2 size={14} className="animate-spin" /> Executing…
            </p>
          )}
          {action.status === "execution_unknown" && (
            <div className="mt-2 space-y-2">
              <p className="text-sm font-medium text-amber-700">
                {errorMsg ||
                  "Execution may have completed, but its status could not be confirmed. Check before trying again."}
              </p>
              <button
                type="button"
                onClick={() => {
                  trackAssistantEvent("action_status_refresh_clicked", {
                    actionType: action.actionType
                  })
                  refreshStatus()
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
              >
                <RefreshCw size={13} /> Check status
              </button>
            </div>
          )}
          {action.status === "rejected" && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500">
              <X size={14} /> Rejected
            </p>
          )}
          {(action.status === "failed" || action.status === "expired") && (
            <p className="mt-2 text-sm font-medium text-red-600">
              {action.status === "expired"
                ? "Proposal expired — ask the agent to propose it again."
                : errorMsg || "Execution failed"}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ApprovalCard
